import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import { GTableBodyProps, GTableColumn } from '../types'
import { StickyEdgeShadow } from '../sticky-edge-shadow'

export const TableBody = <T,>(props: GTableBodyProps<T>) => {
	const { column, cell, isScrolling, isAtEnd = true, variant } = props
	const isDs = variant === 'ds'

	if (!column) return null

	const renderCell = (item: T, column: GTableColumn<T>) => {
		if (column.render) return column.render(item)
		if (column.path) return _.get(item, column.path)
		return <span className="text-xs text-t-default">N/A</span>
	}

	return (
		<td
			style={
				column?.width
					? {
							width: column.width,
							maxWidth: column.width,
							minWidth: column.width,
							// A frozen cell hosts its edge-shadow strip outside its own
							// box, so the td must not clip; the inner div clips instead.
							overflow:
								column.freeze || column.freezeRight ? 'visible' : 'hidden'
					  }
					: {}
			}
			className={twMerge(
				isDs
					? 'whitespace-nowrap px-6 py-4 font-grotesk text-base text-t-default border-b border-b-[rgb(var(--cg-border))]'
					: 'whitespace-nowrap px-4 py-4 text-base text-t-default text-semibold',
				column.hidden ? 'hidden' : '',
				column.mobileHide ? 'hidden md:table-cell' : '',
				column.tabletHide ? 'hidden lg:table-cell' : '',
				// The DS CustomRules recipe stacks body sticky cells at z-[1] with
				// an opaque bg (header cells sit at z-[2]). Without the z-index, a
				// positioned element in a later cell (the Status toggle thumb)
				// paints over the pinned column. The default variant keeps its
				// old classes.
				column.freeze
					? isDs
						? 'sticky left-0 z-[1] bg-[rgb(var(--cg-surface))]'
						: 'sticky left-0'
					: '',
				column.freezeRight
					? isDs
						? 'sticky right-0 z-[1] bg-[rgb(var(--cg-surface))]'
						: 'sticky right-0 z-20 bg-[rgb(var(--cg-surface))]'
					: '',
				!isDs && column.freezeRight && isScrolling
					? 'shadow-[-8px_0_12px_-8px_rgba(16,24,40,0.12)]'
					: '',
				isScrolling ? 'bg-[rgb(var(--cg-surface))]' : ''
			)}
			{...cell.getCellProps()}
		>
			<div
				className={twMerge(
					isDs ? 'flex w-full h-full gap-1' : 'flex w-full h-full px-2',
					column.width && (column.freeze || column.freezeRight)
						? 'overflow-hidden'
						: '',
					column.className || 'justify-end'
				)}
			>
				{renderCell(cell.value, column)}
			</div>
			{isDs && column.freeze && (
				<StickyEdgeShadow show={!!isScrolling} side="right" />
			)}
			{isDs && column.freezeRight && <StickyEdgeShadow show={!isAtEnd} />}
		</td>
	)
}
